"use client";
import React, { useState, useRef, useEffect } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { MdCopyAll } from "react-icons/md";
import {
  HiOutlineDocumentText,
  HiOutlineChevronDown,
  HiOutlineTruck,
  HiOutlineLocationMarker,
  HiOutlineClock,
  HiOutlineShieldCheck,
  HiOutlineSearch,
} from "react-icons/hi";
import { toast, Toaster } from "react-hot-toast";
import API_URLS from "./apiUrls";

export default function Trackerpage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const resultRef = useRef(null);

  const tabs = [
    { label: "AWB", placeholder: "e.g. 90591563134, 90591563135" },
    { label: "Order ID", placeholder: "e.g. ORD1023, ORD1024" },
    { label: "LR Number", placeholder: "e.g. LR10023, LR10024" },
  ];

  const [selectedTab, setSelectedTab] = useState(tabs[0]);
  const [inputValue, setInputValue] = useState("");
  const [trackingResult, setTrackingResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copiedAWB, setCopiedAWB] = useState(null);
  const [selectedLsp, setSelectedLsp] = useState("delhivery");

  // ✅ which pane ("details" | "scans") is open per shipment card
  const [activePane, setActivePane] = useState({});
  const [detailsOpen, setDetailsOpen] = useState({});
  const [selectedShipments, setSelectedShipments] = useState({});

  const handleCopy = (awb) => {
    navigator.clipboard.writeText(awb).then(() => {
      setCopiedAWB(awb);
      toast.success("AWB Number Copied to clipboard!", {
        position: "top-center",
        duration: 2000,
      });
      setTimeout(() => setCopiedAWB(null), 2000);
    });
  };

  const handleDownloadPod = (url) => {
    if (!url) return;
    const normalizedUrl = url.startsWith("http") ? url : `https://${url}`;
    try {
      const link = document.createElement("a");
      link.href = normalizedUrl;
      link.download = "";
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      window.open(normalizedUrl, "_blank", "noopener,noreferrer");
    }
  };

  const getPodUrlsForShipment = (shipment) =>
    (shipment.documentURLs &&
      shipment.documentURLs.length > 0 &&
      shipment.documentURLs) ||
    (shipment.shipmentDocuments &&
      shipment.shipmentDocuments.length > 0 &&
      shipment.shipmentDocuments
        .filter((d) => d.documentURL)
        .map((d) => d.documentURL)) ||
    [];

  const getPrimaryPodUrl = (shipment) => {
    const urls = getPodUrlsForShipment(shipment);
    if (!urls.length) return null;
    const u = urls[0];
    return u.startsWith("http") ? u : `https://${u}`;
  };

  const formatDate = (dateString) => {
    if (dateString) {
      return new Date(dateString).toLocaleString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  // ✅ split a datetime into separate Date / Time strings for the table view
  const splitDateTime = (dateString) => {
    if (!dateString) return { date: "—", time: "—" };
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return { date: "—", time: "—" };
    return {
      date: d.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }),
      time: d.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }),
    };
  };

  const normalizeStatus = (s) => (s || "").toString().trim().toLowerCase();

  const getDisplayStatus = (status) => {
    const normalized = normalizeStatus(status);

    if (normalized === "epod_copy_received") return "Delivered";
    if (normalized === "on hold. finance embargo") return "In Transit";

    return status

  };

  const formatCurrentStatus = (status) => {
    if (!status) return "Status unavailable";
    return getDisplayStatus(status);
  };

  const getStatusTheme = (status) => {
    const normalized = normalizeStatus(status);

    if (normalized === "epod_copy_received") {
      return {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-800",
        dot: "bg-emerald-500",
        ring: "ring-emerald-100",
        solid: "bg-emerald-500",
      };
    }

    if (normalized === "on hold. finance embargo") {
      return {
        bg: "bg-sky-50",
        border: "border-sky-200",
        text: "text-sky-800",
        dot: "bg-sky-500",
        ring: "ring-sky-100",
        solid: "bg-sky-500",
      };
    }

    const displayStatus = normalizeStatus(getDisplayStatus(status));

    if (
      displayStatus.includes("delivered") &&
      !displayStatus.includes("undelivered")
    ) {
      return {
        bg: "bg-emerald-50",
        border: "border-emerald-200",
        text: "text-emerald-800",
        dot: "bg-emerald-500",
        ring: "ring-emerald-100",
        solid: "bg-emerald-500",
      };
    }

    if (normalized.includes("rto") || normalized.includes("return")) {
      return {
        bg: "bg-amber-50",
        border: "border-amber-200",
        text: "text-amber-900",
        dot: "bg-amber-500",
        ring: "ring-amber-100",
        solid: "bg-amber-500",
      };
    }

    if (
      displayStatus.includes("transit") ||
      normalized.includes("transit") ||
      normalized.includes("dispatch") ||
      normalized.includes("ofd") ||
      normalized.includes("pending")
    ) {
      return {
        bg: "bg-sky-50",
        border: "border-sky-200",
        text: "text-sky-800",
        dot: "bg-sky-500",
        ring: "ring-sky-100",
        solid: "bg-sky-500",
      };
    }

    if (
      normalized.includes("undelivered") ||
      normalized.includes("failed") ||
      normalized.includes("cancel")
    ) {
      return {
        bg: "bg-red-50",
        border: "border-red-200",
        text: "text-red-800",
        dot: "bg-red-500",
        ring: "ring-red-100",
        solid: "bg-red-500",
      };
    }

    return {
      bg: "bg-slate-50",
      border: "border-slate-200",
      text: "text-slate-800",
      dot: "bg-slate-500",
      ring: "ring-slate-100",
      solid: "bg-slate-400",
    };
  };

  const isPodEligibleStatus = (shipment) => {
    const status = normalizeStatus(shipment?.currentStatus);
    const lastScan = shipment?.scans?.[shipment?.scans?.length - 1];
    const scanCode = (
      lastScan?.ScanType ||
      lastScan?.StatusCode ||
      lastScan?.Scan ||
      ""
    )
      .toString()
      .trim()
      .toUpperCase();

    if (
      scanCode === "DL" ||
      scanCode.endsWith("-DL") ||
      scanCode.startsWith("DL-")
    ) {
      return true;
    }

    if (status === "epod_copy_received") return true;

    if (status.includes("delivered") && !status.includes("undelivered")) {
      return true;
    }

    return status === "received_by_commercial";
  };

  // ✅ figure out which of the 3 journey steps a shipment is currently on
  const getStepIndex = (shipment) => {
    if (isPodEligibleStatus(shipment)) return 2; // Delivered
    const status = normalizeStatus(shipment?.currentStatus);
    const noScans = !shipment?.scans || shipment.scans.length === 0;

    if (
      noScans ||
      status.includes("booked") ||
      status.includes("created") ||
      status.includes("manifest")
    ) {
      return 0; // Shipped / booked
    }
    return 1; // In transit
  };

  const journeySteps = ["Shipped", "In Transit", "Delivered"];

  const hiddenInstructions = [
    "Added to Bag",
    "Bag Added To Trip",
    "Bag Received at Facility",
    "Bag Removed",
    "Shipment Recieved at Origin Center",
    "Shipment Received at Facility",
  ];

  const getVisibleScans = (shipment) =>
    (shipment.scans || []).filter(
      (s) => !hiddenInstructions.includes(s.Instructions),
    );

  // ✅ Main tracker function with URL update
  const triggerTrack = async (paramName, ids, shouldUpdateUrl = true) => {
    setError("");
    setTrackingResult(null);

    if (!selectedLsp) {
      setError("Please select a logistics service provider (LSP).");
      return;
    }

    if (!ids || ids.length === 0) {
      const labelFromParam =
        paramName === "ref_ids"
          ? "Order ID"
          : paramName === "lrnums"
            ? "LR Number"
            : "AWB";
      setError(`Please enter at least one ${labelFromParam}.`);
      return;
    }

    setLoading(true);
    try {
      const query = encodeURIComponent(ids.join(","));
      const labelParam = encodeURIComponent(selectedTab.label);

      if (shouldUpdateUrl) {
        const newUrl = `?label=${labelParam}&${paramName}=${query}`;
        router.replace(newUrl, { scroll: false });
      }

      const response = await fetch(
        `${API_URLS.tracking}?${paramName}=${query}`,
      );

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      const data = await response.json();
      if (data.Success === false) throw new Error(data.Error);

      setTrackingResult(data);
      setActivePane({});
      setDetailsOpen({});
      setSelectedShipments({});

      setTimeout(() => {
        resultRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);
    } catch (error) {
      console.error("Tracking error:", error);
      setError(
        error.message ||
        "Something went wrong while fetching tracking details.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleTrackOrder = async (shouldUpdateUrl = true) => {
    setError("");
    setTrackingResult(null);

    if (!selectedLsp) {
      setError("Please select a logistics service provider (LSP).");
      return;
    }

    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      setError(`Please enter at least one ${selectedTab.label}.`);
      return;
    }

    const ids = trimmedValue
      .split(",")
      .map((v) => v.trim())
      .filter((v) => v);

    if (ids.length === 0) {
      setError(`Please enter at least one valid ${selectedTab.label}.`);
      return;
    }

    const paramName =
      selectedTab.label === "Order ID"
        ? "ref_ids"
        : selectedTab.label === "LR Number"
          ? "lrnums"
          : "awbs";

    await triggerTrack(paramName, ids, shouldUpdateUrl);
  };

  // ✅ Auto-run on URL load
  useEffect(() => {
    if (!searchParams) return;

    const labelParam = searchParams.get("label");
    const awbs = searchParams.get("awbs");
    const refIds = searchParams.get("ref_ids");
    const lrnums = searchParams.get("lrnums");

    let initialValue = "";
    let initialTab = null;
    let paramNameFromUrl = null;

    if (awbs) {
      initialValue = decodeURIComponent(awbs);
      initialTab = tabs.find((t) => t.label === "AWB");
      paramNameFromUrl = "awbs";
    } else if (refIds) {
      initialValue = decodeURIComponent(refIds);
      initialTab = tabs.find((t) => t.label === "Order ID");
      paramNameFromUrl = "ref_ids";
    } else if (lrnums) {
      initialValue = decodeURIComponent(lrnums);
      initialTab = tabs.find((t) => t.label === "LR Number");
      paramNameFromUrl = "lrnums";
    }

    if (labelParam) {
      const foundTab = tabs.find(
        (t) => t.label === decodeURIComponent(labelParam),
      );
      if (foundTab) initialTab = foundTab;
    }

    if (initialValue && initialTab) {
      setSelectedTab(initialTab);
      setInputValue(initialValue);

      const idsFromUrl = initialValue
        .split(",")
        .map((v) => v.trim())
        .filter((v) => v);

      triggerTrack(paramNameFromUrl, idsFromUrl, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleSelectAll = (checked) => {
    if (!trackingResult) return;
    const next = {};
    trackingResult.forEach((s, i) => {
      const key = s.AWB || s.referenceNo || s.LRNumber || i;
      next[key] = checked;
    });
    setSelectedShipments(next);
  };

  const allSelected =
    !!trackingResult &&
    trackingResult.length > 0 &&
    trackingResult.every(
      (s, i) => selectedShipments[s.AWB || s.referenceNo || s.LRNumber || i],
    );

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 2000,
          style: {
            background: "#363636",
            color: "#fff",
          },
        }}
      />

      {/* ✅ Loader */}
      {loading && (
        <div className="fixed inset-0 z-50 bg-white/40 backdrop-blur-sm flex items-center justify-center">
          <div className="text-center">
            <svg
              className="animate-spin h-16 w-16 text-emerald-600 mx-auto mb-4"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8H4z"
              ></path>
            </svg>
            <p className="text-slate-700 text-lg font-semibold">
              Tracking in progress...
            </p>
          </div>
        </div>
      )}

      {/* ✅ Top accent strip */}
      <div className="h-2 w-full bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400" />

      {/* ✅ Info banner */}
      <div className="border-b border-sky-100 bg-sky-50 px-4 py-3 text-center text-xs text-sky-900 sm:text-sm">
        This page shows real-time tracking updates for your shipment. Enter your{" "}
        <span className="font-semibold">AWB</span>,{" "}
        <span className="font-semibold">Order ID</span> or{" "}
        <span className="font-semibold">LR Number</span> in the panel to check
        status.
      </div>

      <div className="min-h-screen bg-slate-100 px-4 py-8 sm:px-6 lg:px-10">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          {/* ================= LEFT: Tracking details ================= */}
          <div className="min-w-0">
            {/* Section header bar */}
            <div className="overflow-hidden rounded-t-xl bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 px-5 py-3 shadow-sm">
              <h1 className="text-base font-bold tracking-wide text-white sm:text-lg">
                Shipment Tracker — Tracking Details
              </h1>
            </div>

            <div className="rounded-b-xl border border-t-0 border-slate-200 bg-white shadow-sm">
              {error && (
                <div className="mx-5 mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                  {error}
                </div>
              )}

              {!trackingResult && !error && (
                <div className="px-6 py-10 sm:px-10">
                  {/* Intro / hero strip */}
                  <div className="flex flex-col items-start gap-4 rounded-2xl bg-gradient-to-br from-emerald-50 via-sky-50 to-white p-6 sm:flex-row sm:items-center sm:p-8">
                    <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-gradient-to-br  from-blue-900 via-blue-800 to-blue-900 shadow-md">
                      <HiOutlineTruck className="h-7 w-7" />
                    </span>
                    <div>
                      <h2 className="text-lg font-bold text-slate-900 sm:text-xl">
                        Track your shipment in real time
                      </h2>
                      <p className="mt-1 max-w-lg text-sm text-slate-500">
                        Enter your AWB, Order ID or LR Number in the panel on
                        the right and hit{" "}
                        <span className="font-semibold text-emerald-700">
                          GO
                        </span>{" "}
                        — live location, delivery status and proof of delivery
                        will show up right here.
                      </p>
                    </div>
                  </div>

                  {/* Feature grid */}
                  <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    {[
                      {
                        icon: HiOutlineLocationMarker,
                        title: "Live Location",
                        desc: "See the current hub or city your shipment is moving through.",
                      },
                      {
                        icon: HiOutlineClock,
                        title: "Delivery Timeline",
                        desc: "Every scan, from pickup to delivery, in one clean timeline.",
                      },
                      {
                        icon: HiOutlineDocumentText,
                        title: "Proof of Delivery",
                        desc: "Download the signed POD as soon as it's delivered.",
                      },
                      {
                        icon: HiOutlineShieldCheck,
                        title: "Verified Status",
                        desc: "Status pulled straight from the courier's own scans.",
                      },
                    ].map((f, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md"
                      >
                        <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
                          <f.icon className="h-5 w-5" />
                        </span>
                        <p className="mt-3 text-sm font-bold text-slate-800">
                          {f.title}
                        </p>
                        <p className="mt-1 text-xs leading-relaxed text-slate-500">
                          {f.desc}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* How it works */}
                  <div className="mt-8">
                    <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      How it works
                    </p>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      {[
                        {
                          step: "1",
                          title: "Enter your number",
                          desc: "AWB, Order ID or LR Number — pick whichever you have.",
                        },
                        {
                          step: "2",
                          title: "Hit GO",
                          desc: "We fetch the latest scans directly from the courier.",
                        },
                        {
                          step: "3",
                          title: "View live status",
                          desc: "Track the journey and grab the POD once delivered.",
                        },
                      ].map((s, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-sky-600 text-sm font-bold text-white">
                            {s.step}
                          </span>
                          <div>
                            <p className="text-sm font-bold text-slate-800">
                              {s.title}
                            </p>
                            <p className="mt-0.5 text-xs text-slate-500">
                              {s.desc}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-8 flex items-center gap-2 rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-400">
                    <HiOutlineSearch className="h-4 w-4 shrink-0" />
                    No shipment tracked yet — your results will appear right
                    here.
                  </div>
                </div>
              )}

              {trackingResult && trackingResult.length > 0 && (
                <>
                  {/* <label className="flex items-center gap-2 border-b border-slate-100 px-5 py-3 text-sm font-medium text-slate-600">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    Select / Deselect All
                  </label> */}

                  <div ref={resultRef}>
                    {trackingResult.map((shipment, shipmentIndex) => {
                      const shipmentKey =
                        shipment.AWB ||
                        shipment.referenceNo ||
                        shipment.LRNumber ||
                        shipmentIndex;
                      const headerPodUrl = getPrimaryPodUrl(shipment);
                      const showHeaderViewPod =
                        isPodEligibleStatus(shipment) && headerPodUrl;
                      const theme = getStatusTheme(shipment.currentStatus);
                      const stepIndex = getStepIndex(shipment);
                      const pane = activePane[shipmentKey] || "scans";
                      const isMoreOpen = !!detailsOpen[shipmentKey];
                      const visibleScans = getVisibleScans(shipment);

                      const displayId =
                        shipment.AWB ||
                        shipment.referenceNo ||
                        shipment.LRNumber;
                      const displayLabel = shipment.LRNumber
                        ? "LR Number"
                        : shipment.AWB
                          ? "AWB"
                          : "Order ID";

                      const detailItems = [
                        {
                          label: "Status",
                          value: formatCurrentStatus(shipment.currentStatus),
                        },
                        { label: "Origin", value: shipment.origin },
                        { label: "Destination", value: shipment.destination },
                        {
                          label: "Expected Delivery",
                          value: formatDate(shipment.estimatedDeliveryDate),
                        },
                        { label: "Order Type", value: shipment.orderType },
                        { label: "Reference No", value: shipment.referenceNo },
                        { label: "From", value: shipment.currentLocation },
                        { label: "To", value: shipment.lastLocation },
                      ].filter((item) => item.value);

                      return (
                        <div
                          key={shipmentKey}
                          className="border-b border-slate-200 px-5 py-5 last:border-b-0 sm:px-6"
                        >
                          {/* waybill + status row */}
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <label className="flex min-w-0 items-start gap-3">
                              {/* <input
                                type="checkbox"
                                checked={!!selectedShipments[shipmentKey]}
                                onChange={(e) =>
                                  setSelectedShipments((prev) => ({
                                    ...prev,
                                    [shipmentKey]: e.target.checked,
                                  }))
                                }
                                className="mt-1 h-4 w-4 shrink-0 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                              /> */}
                              <span className="min-w-0">
                                <span className="block text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                                  {displayLabel}
                                </span>
                                <span className="flex flex-wrap items-center gap-2">
                                  <span className="truncate text-lg font-bold text-slate-900">
                                    {displayId}
                                  </span>
                                  {displayId && (
                                    <button
                                      onClick={() => handleCopy(displayId)}
                                      type="button"
                                      className="inline-flex cursor-pointer shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
                                    >
                                      <MdCopyAll />
                                      {copiedAWB === displayId
                                        ? "Copied"
                                        : "Copy"}
                                    </button>
                                  )}
                                </span>
                              </span>
                            </label>

                            <span
                              className={[
                                "inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-semibold ring-1",
                                theme.bg,
                                theme.border,
                                theme.text,
                                theme.ring,
                              ].join(" ")}
                            >
                              <span
                                className={[
                                  "h-2 w-2 rounded-full",
                                  theme.dot,
                                ].join(" ")}
                              />
                              {formatCurrentStatus(shipment.currentStatus)}
                            </span>
                          </div>

                          {/* delivery date row */}
                          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-sm">
                              <span className="font-semibold text-emerald-700">
                                {stepIndex === 2
                                  ? "Date of Delivery"
                                  : "Est. Delivery"}
                              </span>{" "}
                              <span className="font-bold text-slate-800">
                                {formatDate(shipment.estimatedDeliveryDate) ||
                                  "—"}
                              </span>
                            </p>
                            {shipment.returnReason && (
                              <p className="text-sm">
                                <span className="font-semibold text-emerald-700">
                                  EDD Reason:
                                </span>{" "}
                                <span className="font-bold text-slate-800">
                                  {shipment.returnReason}
                                </span>
                              </p>
                            )}

                            {detailItems.length > 0 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setDetailsOpen((prev) => ({
                                    ...prev,
                                    [shipmentKey]: !prev[shipmentKey],
                                  }))
                                }
                                className="inline-flex items-center gap-1 self-start text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                              >
                                More Details
                                <HiOutlineChevronDown
                                  className={[
                                    "h-4 w-4 transition-transform",
                                    isMoreOpen ? "rotate-180" : "",
                                  ].join(" ")}
                                />
                              </button>
                            )}
                          </div>

                          {isMoreOpen && detailItems.length > 0 && (
                            <div className="mt-3 grid grid-cols-1 gap-3 rounded-lg bg-emerald-50/60 p-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
                              {detailItems.map((item, i) => (
                                <div key={i}>
                                  <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700/70">
                                    {item.label}
                                  </p>
                                  <p className="mt-1 rounded-md bg-white px-3 py-2 font-semibold text-slate-800 shadow-sm ring-1 ring-emerald-100">
                                    {item.value}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* stepper */}
                          <div className="mt-6 px-1 sm:px-4">
                            {/* <div className="relative flex items-center justify-between">
                              <div className="absolute left-0 right-0 top-[11px] h-1 rounded-full bg-slate-200" />
                              <div
                                className="absolute left-0 top-[11px] h-1 rounded-full bg-emerald-500 transition-all duration-700"
                                style={{
                                  width:
                                    stepIndex === 0
                                      ? "0%"
                                      : stepIndex === 1
                                        ? "50%"
                                        : "100%",
                                }}
                              />
                              {journeySteps.map((step, i) => {
                                const reached = i <= stepIndex;
                                return (
                                  <div
                                    key={step}
                                    className="relative z-10 flex flex-col items-center gap-2 text-center"
                                    style={{ width: "33%" }}
                                  >
                                    <span
                                      className={[
                                        "h-6 w-6 rounded-full border-2 transition-colors",
                                        reached
                                          ? "border-transparent bg-emerald-500"
                                          : "border-slate-300 bg-white",
                                      ].join(" ")}
                                    />
                                    <span
                                      className={[
                                        "text-xs font-semibold sm:text-sm",
                                        reached
                                          ? "text-slate-900"
                                          : "text-slate-400",
                                      ].join(" ")}
                                    >
                                      {step}
                                    </span>
                                  </div>
                                );
                              })}
                            </div> */}
                          </div>

                          {/* tabs */}
                          <div className="mt-5 flex gap-6 border-b border-slate-200">
                            {[
                              { key: "details", label: "Shipment Details" },
                              { key: "scans", label: "Status and Scan" },
                            ].map((t) => (
                              <button
                                key={t.key}
                                type="button"
                                onClick={() =>
                                  setActivePane((prev) => ({
                                    ...prev,
                                    [shipmentKey]: t.key,
                                  }))
                                }
                                className={[
                                  "relative -mb-px cursor-pointer pb-2.5 text-sm font-semibold transition",
                                  pane === t.key
                                    ? "text-emerald-700"
                                    : "text-slate-400 hover:text-slate-600",
                                ].join(" ")}
                              >
                                {t.label}
                                {pane === t.key && (
                                  <span className="absolute -bottom-px left-0 right-0 h-[2px] rounded-full bg-emerald-600" />
                                )}
                              </button>
                            ))}
                          </div>

                          {/* details pane */}
                          {pane === "details" && (
                            <div className="grid grid-cols-1 gap-3 py-5 text-sm sm:grid-cols-2 lg:grid-cols-3">
                              {[
                                { label: "AWB Number", value: shipment.AWB },
                                ...detailItems,
                              ].map(
                                (item, i) =>
                                  item.value && (
                                    <div key={i}>
                                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                                        {item.label}
                                      </p>
                                      <div className="mt-1 rounded-md bg-emerald-50 px-3 py-2 font-semibold text-emerald-900 shadow-sm ring-1 ring-emerald-100">
                                        {item.value}
                                      </div>
                                    </div>
                                  ),
                              )}
                            </div>
                          )}

                          {/* scans table pane */}
                          {pane === "scans" && (
                            <div className="overflow-x-auto py-5">
                              {visibleScans.length === 0 ? (
                                <p className="py-6 text-center text-sm font-medium text-slate-400">
                                  No scan events yet for this shipment.
                                </p>
                              ) : (
                                <table className="w-full min-w-[560px] border-separate border-spacing-0 text-sm">
                                  <thead>
                                    <tr className="bg-emerald-50 text-left text-xs font-semibold uppercase tracking-wide text-emerald-800">
                                      <th className="rounded-l-lg px-4 py-3">
                                        Location
                                      </th>
                                      <th className="px-4 py-3">Details</th>
                                      <th className="px-4 py-3">Date</th>
                                      <th className="rounded-r-lg px-4 py-3">
                                        Time
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {[...visibleScans]
                                      .reverse()
                                      .map((scan, i) => {
                                        const { date, time } = splitDateTime(
                                          scan.ScanDateTime,
                                        );
                                        const isDeliveredScan =
                                          normalizeStatus(scan.Scan).includes(
                                            "delivered",
                                          ) ||
                                          normalizeStatus(
                                            scan.Instructions,
                                          ).includes("delivered") ||
                                          (
                                            scan?.ScanType ||
                                            scan?.StatusCode ||
                                            scan?.Scan ||
                                            ""
                                          )
                                            .toString()
                                            .trim()
                                            .toUpperCase()
                                            .endsWith("-DL");

                                        const podUrls =
                                          getPodUrlsForShipment(shipment);
                                        const hasPod =
                                          isPodEligibleStatus(shipment) &&
                                          podUrls.length > 0;
                                        const podUrl = hasPod
                                          ? getPrimaryPodUrl(shipment)
                                          : null;
                                        const showPodHere =
                                          hasPod && podUrl && isDeliveredScan;

                                        return (
                                          <tr
                                            key={i}
                                            className="odd:bg-white even:bg-emerald-50/40 border-b border-slate-100 last:border-b-0"
                                          >
                                            <td className="px-4 py-3 font-semibold text-slate-800">
                                              {scan.ScannedLocation || "—"}
                                            </td>
                                            <td className="px-4 py-3 text-slate-600">
                                              <div className="flex flex-wrap items-center gap-2">
                                                <span>
                                                  {scan.Instructions ||
                                                    scan.Scan ||
                                                    "—"}
                                                </span>
                                                {showPodHere && (
                                                  <button
                                                    type="button"
                                                    className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100"
                                                    onClick={() =>
                                                      handleDownloadPod(podUrl)
                                                    }
                                                  >
                                                    <HiOutlineDocumentText className="h-3.5 w-3.5" />
                                                    VIEW POD
                                                  </button>
                                                )}
                                              </div>
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                                              {date}
                                            </td>
                                            <td className="whitespace-nowrap px-4 py-3 tabular-nums text-slate-600">
                                              {time}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}

                          {/* footer actions */}
                          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
                            {/* <select className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 shadow-sm focus:border-emerald-400 focus:outline-none">
                              <option>Select</option>
                              <option>Mark as reviewed</option>
                            </select> */}

                            {isPodEligibleStatus(shipment) &&
                              getPrimaryPodUrl(shipment) && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleDownloadPod(
                                      getPrimaryPodUrl(shipment),
                                    )
                                  }
                                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
                                >
                                  <HiOutlineDocumentText className="h-4 w-4" />
                                  Download POD
                                </button>
                              )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ================= RIGHT: Sticky tracker input sidebar ================= */}
          <aside className="h-fit lg:sticky lg:top-6">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-sky-600 text-white shadow-sm">
                  <HiOutlineTruck className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-bold leading-tight text-slate-900">
                    Track Your Shipment
                  </p>
                  <p className="text-[11px] text-slate-400">
                    Real-time updates
                  </p>
                </div>
              </div>

              <p className="mb-2 text-xs font-semibold text-slate-500">
                Enter your:
              </p>
              <div className="mb-3 flex flex-col gap-2">
                {tabs.map((tab) => (
                  <label
                    key={tab.label}
                    className="flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700"
                  >
                    <input
                      type="radio"
                      name="tab-select"
                      checked={selectedTab.label === tab.label}
                      onChange={() => setSelectedTab(tab)}
                      className="h-4 w-4 border-slate-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    {tab.label}
                  </label>
                ))}
              </div>

              <textarea
                rows={4}
                placeholder={selectedTab.placeholder}
                className="block w-full resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 shadow-inner outline-none transition focus:border-emerald-400 focus:bg-white"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleTrackOrder();
                  }
                }}
              />
              <p className="mt-1.5 text-[11px] text-slate-400">
                For multiple queries use commas, e.g: 79034111122, 79034111041
              </p>

              <button
                type="button"
                onClick={() => handleTrackOrder()}
                className="mt-4 w-full cursor-pointer rounded-lg bg-gradient-to-r from-emerald-500 to-sky-600 py-2.5 text-sm font-bold text-white shadow-sm transition hover:brightness-105"
              >
                GO
              </button>
            </div>

          </aside>
        </div>
      </div>
    </>
  );
}

